import axios from 'axios';
import { katana, plasma } from 'src/config/viem';
import { createPublicClient, http } from 'viem';
import {
  arbitrum,
  avalanche,
  avalancheFuji,
  base,
  bsc,
  celo,
  gnosis,
  ink,
  mainnet,
  mantle,
  mode,
  monad,
  optimism,
  polygon,
  polygonZkEvm,
  stable,
  zksync,
} from 'viem/chains';

export interface TokenMetadata {
  symbol: string;
  name: string;
  decimals: number;
  logoURI?: string;
  isVerified: boolean;
}

export interface TokenResolutionResult {
  metadata: TokenMetadata | null;
  source: 'internal' | 'external' | 'fallback';
  error?: string;
}

interface CoinGeckoTokenResponse {
  symbol: string;
  name: string;
  decimals: number;
  image?: {
    large?: string;
    small?: string;
  };
}

interface EtherscanTokenResponse {
  status: string;
  message: string;
  result: Array<{
    contractAddress: string;
    tokenName: string;
    symbol: string;
    decimals: string;
  }>;
}

// Cache for token metadata to avoid repeated API calls
const tokenCache = new Map<string, { data: TokenMetadata; timestamp: number }>();
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

// Balancer Pool Token ABI for name() and symbol() functions
const BALANCER_POOL_ABI = [
  {
    inputs: [],
    name: 'name',
    outputs: [{ name: '', type: 'string' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'symbol',
    outputs: [{ name: '', type: 'string' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'decimals',
    outputs: [{ name: '', type: 'uint8' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const;

// Chain configuration for viem clients (all networks supporting token holders)
const chainMap: Record<number, any> = {
  // Mainnet networks with canBeUsedForTokenHolders: true
  1: mainnet, // Ethereum
  137: polygon, // Polygon
  42161: arbitrum, // Arbitrum
  56: bsc, // BNB (Binance Smart Chain)
  8453: base, // Base
  43114: avalanche, // Avalanche
  5000: mantle, // Mantle
  34443: mode, // Mode
  143: monad, // Monad
  747474: katana, // Katana
  9745: plasma, // Plasma
  988: stable, // Stable
  57073: ink, // Ink
  // Additional supported networks
  10: optimism, // Optimism (Balancer supported)
  100: gnosis, // Gnosis (Balancer supported)
  1101: polygonZkEvm, // Polygon zkEVM (Balancer supported)
  324: zksync, // zkSync Era (Balancer supported)
  42220: celo, // Celo (Balancer supported)

  // Testnet networks with canBeUsedForTokenHolders: true
  43113: avalancheFuji, // Avalanche Fuji
};

/**
 * Generates cache key for token
 */
function getCacheKey(address: string, chainId: number): string {
  return `${chainId}:${address.toLowerCase()}`;
}

/**
 * Checks if cached data is still valid
 */
function isCacheValid(timestamp: number): boolean {
  return Date.now() - timestamp < CACHE_DURATION;
}

/**
 * Fetches token metadata from CoinGecko API
 */
async function fetchFromCoinGecko(address: string, chainId: number): Promise<TokenMetadata | null> {
  try {
    // Map chainId to CoinGecko platform ID
    const platformMap: Record<number, string> = {
      1: 'ethereum',
      137: 'polygon-pos',
      42161: 'arbitrum-one',
      8453: 'base',
      10: 'optimistic-ethereum',
      56: 'binance-smart-chain',
      43114: 'avalanche',
    };

    const platform = platformMap[chainId];
    if (!platform) {
      return null;
    }

    const response = await axios.get<CoinGeckoTokenResponse>(
      `https://api.coingecko.com/api/v3/coins/${platform}/contract/${address}`,
      {
        timeout: 5000,
        headers: {
          Accept: 'application/json',
        },
      },
    );

    const data = response.data;
    return {
      symbol: data.symbol.toUpperCase(),
      name: data.name,
      decimals: data.decimals,
      logoURI: data.image?.small || data.image?.large,
      isVerified: true,
    };
  } catch (error) {
    console.warn('CoinGecko API error:', error);
    return null;
  }
}

/**
 * Fetches token metadata directly from onchain contract calls
 */
async function fetchFromOnchain(address: string, chainId: number): Promise<TokenMetadata | null> {
  try {
    const chain = chainMap[chainId];
    if (!chain || chain === undefined) {
      // Chain not supported by viem yet, skip onchain calls
      return null;
    }

    const publicClient = createPublicClient({
      chain,
      transport: http(),
    });

    // Make parallel calls to get name, symbol, and decimals
    const [name, symbol, decimals] = await Promise.allSettled([
      publicClient.readContract({
        address: address as `0x${string}`,
        abi: BALANCER_POOL_ABI,
        functionName: 'name',
      }),
      publicClient.readContract({
        address: address as `0x${string}`,
        abi: BALANCER_POOL_ABI,
        functionName: 'symbol',
      }),
      publicClient.readContract({
        address: address as `0x${string}`,
        abi: BALANCER_POOL_ABI,
        functionName: 'decimals',
      }),
    ]);

    // Extract successful results
    const nameResult = name.status === 'fulfilled' ? name.value : null;
    const symbolResult = symbol.status === 'fulfilled' ? symbol.value : null;
    const decimalsResult = decimals.status === 'fulfilled' ? Number(decimals.value) : 18;

    if (nameResult && symbolResult) {
      return {
        symbol: String(symbolResult).toUpperCase(),
        name: String(nameResult),
        decimals: decimalsResult,
        isVerified: true,
      };
    }

    return null;
  } catch (error) {
    console.warn('Onchain contract call error:', error);
    return null;
  }
}

/**
 * Fetches token metadata from Etherscan-like APIs
 */
async function fetchFromEtherscan(address: string, chainId: number): Promise<TokenMetadata | null> {
  try {
    // Map chainId to Etherscan API endpoints (all networks with canBeUsedForTokenHolders)
    const apiMap: Record<number, { url: string; key?: string }> = {
      // Major networks with Etherscan-like APIs
      1: { url: 'https://api.etherscan.io/api' }, // Ethereum
      137: { url: 'https://api.polygonscan.com/api' }, // Polygon
      42161: { url: 'https://api.arbiscan.io/api' }, // Arbitrum
      8453: { url: 'https://api.basescan.org/api' }, // Base
      10: { url: 'https://api-optimistic.etherscan.io/api' }, // Optimism
      56: { url: 'https://api.bscscan.com/api' }, // BSC
      43114: { url: 'https://api.snowtrace.io/api' }, // Avalanche
      5000: { url: 'https://api.mantlescan.xyz/api' }, // Mantle
      100: { url: 'https://api.gnosisscan.io/api' }, // Gnosis
      324: { url: 'https://api-era.zksync.network/api' }, // zkSync Era
      1101: { url: 'https://api-zkevm.polygonscan.com/api' }, // Polygon zkEVM
      42220: { url: 'https://api.celoscan.io/api' }, // Celo
      43113: { url: 'https://api-testnet.snowtrace.io/api' }, // Avalanche Fuji
      // Note: Newer/smaller networks may not have Etherscan-like APIs yet
    };

    const apiConfig = apiMap[chainId];
    if (!apiConfig) {
      return null;
    }

    const params = new URLSearchParams({
      module: 'token',
      action: 'tokeninfo',
      contractaddress: address,
      tag: 'latest',
    });

    const response = await axios.get<EtherscanTokenResponse>(`${apiConfig.url}?${params}`, {
      timeout: 5000,
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.data.status === '1' && response.data.result?.length > 0) {
      const token = response.data.result[0];
      return {
        symbol: token.symbol.toUpperCase(),
        name: token.tokenName,
        decimals: parseInt(token.decimals),
        isVerified: true,
      };
    }

    return null;
  } catch (error) {
    console.warn('Etherscan API error:', error);
    return null;
  }
}

/**
 * Resolves token metadata from external APIs with fallback strategy
 */
export async function resolveTokenMetadata(address: string, chainId: number): Promise<TokenResolutionResult> {
  // Validate address format
  if (!address || !/^0x[a-fA-F0-9]{40}$/.test(address)) {
    return {
      metadata: null,
      source: 'fallback',
      error: 'Invalid address format',
    };
  }

  const cacheKey = getCacheKey(address, chainId);

  // Check cache first
  const cached = tokenCache.get(cacheKey);
  if (cached && isCacheValid(cached.timestamp)) {
    return {
      metadata: cached.data,
      source: 'external',
    };
  }

  try {
    // Try CoinGecko first (more reliable for token metadata)
    let metadata = await fetchFromCoinGecko(address, chainId);

    // Fallback to Etherscan if CoinGecko fails
    if (!metadata) {
      metadata = await fetchFromEtherscan(address, chainId);
    }

    // Fallback to onchain calls if external APIs fail
    if (!metadata) {
      metadata = await fetchFromOnchain(address, chainId);
    }

    if (metadata) {
      // Cache the result
      tokenCache.set(cacheKey, {
        data: metadata,
        timestamp: Date.now(),
      });

      return {
        metadata,
        source: 'external',
      };
    }

    return {
      metadata: null,
      source: 'fallback',
      error: 'Token not found in external APIs',
    };
  } catch (error) {
    console.error('Token metadata resolution error:', error);
    return {
      metadata: null,
      source: 'fallback',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Resolves Balancer LP token metadata
 */
export async function resolveBalancerPoolMetadata(
  poolAddress: string,
  chainId: number,
): Promise<TokenResolutionResult> {
  // Validate address format
  if (!poolAddress || !/^0x[a-fA-F0-9]{40}$/.test(poolAddress)) {
    return {
      metadata: null,
      source: 'fallback',
      error: 'Invalid address format',
    };
  }

  const cacheKey = getCacheKey(poolAddress, chainId);

  // Check cache first
  const cached = tokenCache.get(cacheKey);
  if (cached && isCacheValid(cached.timestamp)) {
    return {
      metadata: cached.data,
      source: 'external',
    };
  }

  try {
    // Primary: Try onchain contract calls first (most reliable)
    let metadata = await fetchFromOnchain(poolAddress, chainId);

    if (metadata) {
      // Cache the result
      tokenCache.set(cacheKey, {
        data: metadata,
        timestamp: Date.now(),
      });

      return {
        metadata,
        source: 'external',
      };
    }

    // Secondary: Try Balancer subgraph if onchain fails
    metadata = await fetchFromBalancerSubgraph(poolAddress, chainId);

    if (metadata) {
      // Cache the result
      tokenCache.set(cacheKey, {
        data: metadata,
        timestamp: Date.now(),
      });

      return {
        metadata,
        source: 'external',
      };
    }

    // Tertiary: Fallback to regular token resolution (CoinGecko, Etherscan)
    return resolveTokenMetadata(poolAddress, chainId);
  } catch (error) {
    console.warn('Balancer pool resolution error:', error);
    return {
      metadata: null,
      source: 'fallback',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Fetches Balancer pool metadata from subgraph
 */
async function fetchFromBalancerSubgraph(poolAddress: string, chainId: number): Promise<TokenMetadata | null> {
  try {
    // Balancer v2 subgraph endpoints (development/rate-limited endpoints)
    // Note: For production use, would need API keys for gateway endpoints
    const subgraphMap: Record<number, string> = {
      1: 'https://api.studio.thegraph.com/query/75376/balancer-v2/version/latest', // Ethereum
      137: 'https://api.studio.thegraph.com/query/75376/balancer-polygon-v2/version/latest', // Polygon
      42161: 'https://api.studio.thegraph.com/query/75376/balancer-arbitrum-v2/version/latest', // Arbitrum
      10: 'https://api.studio.thegraph.com/query/75376/balancer-optimism-v2/version/latest', // Optimism
      8453: 'https://api.studio.thegraph.com/query/75376/balancer-base-v2/version/latest', // Base
      100: 'https://api.studio.thegraph.com/query/75376/balancer-gnosis-v2/version/latest', // Gnosis
      43114: 'https://api.studio.thegraph.com/query/75376/balancer-avalanche-v2/version/latest', // Avalanche
      1101: 'https://api.studio.thegraph.com/query/75376/balancer-zkevm-v2/version/latest', // Polygon zkEVM
      34443: 'https://api.studio.thegraph.com/query/75376/balancer-mode-v2/version/latest', // Mode Network
      // Note: BSC, Celo, zkSync Era, Fraxtal may not have active v2 subgraphs yet
      // These networks will fall back to onchain resolution which is more reliable
    };

    const subgraphUrl = subgraphMap[chainId];
    if (!subgraphUrl) {
      return null;
    }

    const query = `
      query GetPool($poolAddress: String!) {
        pool(id: $poolAddress) {
          id
          name
          symbol
          tokens {
            symbol
            weight
          }
        }
      }
    `;

    const response = await axios.post(
      subgraphUrl,
      {
        query,
        variables: { poolAddress: poolAddress.toLowerCase() },
      },
      {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    // Enhanced error logging for debugging
    if (response.data?.errors) {
      console.warn('Balancer subgraph query errors:', response.data.errors);
    }

    const pool = response.data?.data?.pool;
    if (pool) {
      // Generate a descriptive symbol from pool tokens
      const tokenSymbols = pool.tokens.map((t: any) => t.symbol).join('-');
      const symbol = pool.symbol || `BPT-${tokenSymbols}`;

      return {
        symbol: symbol.toUpperCase(),
        name: pool.name || `Balancer Pool Token (${tokenSymbols})`,
        decimals: 18, // BPT tokens are typically 18 decimals
        isVerified: true,
      };
    }

    return null;
  } catch (error) {
    console.warn('Balancer subgraph error:', error);
    return null;
  }
}

/**
 * Clears the token metadata cache
 */
export function clearTokenCache(): void {
  tokenCache.clear();
}

/**
 * Gets cache statistics for debugging
 */
export function getCacheStats(): { size: number; keys: string[] } {
  return {
    size: tokenCache.size,
    keys: Array.from(tokenCache.keys()),
  };
}
